import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { of, from } from 'rxjs';
import { debounceTime, switchMap, tap, catchError, finalize } from 'rxjs/operators';
import { ReservaDoctorService } from 'src/app/services/reserva-doctor.service';
import { UsuarioService } from '../../../services/usuario.service';
import { EncriptacionService } from 'src/app/services/encriptacion.service';
import { ActivatedRoute, Router } from '@angular/router';
import { GeneralService } from 'src/app/services/general.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-reserva-doctor',
  templateUrl: './reserva-doctor.component.html',
  styleUrls: ['./reserva-doctor.component.css']
})
export class ReservaDoctorComponent implements OnInit {

  // ---------------------------
  // Encabezado y estado general
  // ---------------------------
  _title = 'Mi agenda';
  _subtitle = 'Agendamiento de Pacientes';
  public usuario: any;
  steps: any = { searchPatient: true, selectDate: false, payment: false, showSummary: false };
  showSuccessMessage = false;

  // ---------------------------
  // Autocomplete búsqueda RUT/DNI
  // ---------------------------
  searchCtrl = new FormControl('');
  resultados: any[] = [];
  loading = false;
  showDropdown = false;
  isRutLocked = false;
  minSearchLen = 4;
  tooShort = false;

  // ---------------------------
  // Datos y catálogos
  // ---------------------------
  infoRut: any = {};
  infoDoctor: any = {};
  areasMedicas: any[] = [];
  tarifas: any[] = [];
  showAreaCode = true;
  isNewUser = false;
  areaCodes: any[] = [];
  selectedAreaCode: any = { cou_phone_code: '56', cou_flag: 'fi-cl', cou_min_phone: 9, cou_max_phone: 9 };

  // ---------------------------
  // Archivos adjuntos (step 3)
  // ---------------------------
  selectedFiles: any[] = [];
  showDocumentoModal = false;
  modalFileNamePreview = '';
  documentoNombreResumen: string | null = null;  // 👈 acá

  acompanantesPaciente: any[] = [];
  loadingAcompanantes = false;

  public modalDocForm = this.fb.group({
    displayName: ['', Validators.required],
    file: [null, Validators.required]
  });

  // ---------------------------
  // Formulario principal (Steps 1–3)
  // ---------------------------
  public patientForm = this.fb.group({
    // step1
    use_id: [''],
    use_rut: [
      '',
      [
        Validators.required,
        Validators.pattern(/^[0-9]{1,8}-?[0-9Kk]$/)
      ]
    ], use_name: ['', [Validators.required]],
    paterno: ['', [Validators.required, Validators.minLength(3), Validators.pattern("^[a-zA-ZáéíóúÁÉÍÓÚñÑçÇ' ]+$")]],
    materno: ['', [Validators.minLength(3), Validators.pattern("^[a-zA-ZáéíóúÁÉÍÓÚñÑçÇ' ]+$")]],
    nacimiento: ['', Validators.required],
    telefonoCodigo: ['56', [Validators.required]],
    telefono: ['', [Validators.required, Validators.pattern("^[0-9]*$")]],
    email: ['', [Validators.required, Validators.email, Validators.minLength(3)]],

    // step2 (selección fecha/hora)
    sch_date: [''],
    sch_hou_id: [null],
    sch_from: [''],
    sch_to: [''],

    // step3
    area_medica: [null],
    acompaniante: [null],
    motivo: [''],
    tarifa: [null],
    tarifa_custom_monto: [null]

  });
  step1Unlocked = false;

  private step1EditableControls = [
    'use_name',
    'paterno',
    'materno',
    'nacimiento',
    'telefonoCodigo',
    'telefono',
    'email'
  ];
  searchRutInvalid = false;
  constructor(
    public reservaDoctor: ReservaDoctorService,
    private fb: FormBuilder,
    private usuarioService: UsuarioService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private generalService: GeneralService

  ) { }

  // =========================================================
  // LIFECYCLE
  // =========================================================
  ngOnInit(): void {
    this.usuario = new EncriptacionService().getUserInfo();
    this.getInfoDoctor();
    this.getCodPaises();
    this.initAutocomplete();

    // Recalcula tarifas según el área seleccionada (step 3)
    this.patientForm.get('area_medica')?.valueChanges.subscribe((areaSel: any) => {
      this.tarifas = this.buildTarifasFromArea(areaSel);

      // default: primera tarifa disponible
      this.patientForm.patchValue(
        { tarifa: this.tarifas[0] ?? null },
        { emitEvent: true } // ✅ importante para disparar validaciones de tarifa custom
      );
    });

    // ✅ Si es "Personalizada", exigir monto; si no, limpiar
    this.patientForm.get('tarifa')?.valueChanges.subscribe((tar: any) => {
      const customCtrl = this.patientForm.get('tarifa_custom_monto');

      if (tar?.tipo === 'custom') {
        customCtrl?.setValidators([
          Validators.required,
          Validators.pattern(/^\$\d{1,3}(\.\d{3})*$/),
          this.maxMontoValidator(1000000)
        ]);
      } else {
        customCtrl?.clearValidators();
        customCtrl?.setValue(null, { emitEvent: false });
      }

      customCtrl?.updateValueAndValidity({ emitEvent: false });
    });
    this.lockStep1Form();
  }


  // =========================================================
  // CARGA DE DATOS
  // =========================================================
  getInfoDoctor(params = this.usuario._id) {
    this.reservaDoctor.getInfoDoctor(params)
      .then((resp: any) => {
        this.infoDoctor = resp?.data || {};
        console.log(this.infoDoctor);
        this.buildAreasDesdeDoctor();
      })
      .catch(() => { this.infoDoctor = {}; this.areasMedicas = []; });
  }

  getCodPaises() {
    this.usuarioService.getCodPaises()
      .then((resp: any) => { this.areaCodes = resp as any[]; })
      .catch(() => { this.areaCodes = []; });
  }

  private buildAreasDesdeDoctor() {
    const lista = this.infoDoctor?.listSpecialitiesDoctors
      || this.infoDoctor?.data?.listSpecialitiesDoctors
      || [];

    this.areasMedicas = lista.map((s: any) => {
      const mostrarSub = s?.sub_spe_name && s.sub_spe_name !== '[Todas]';
      const nombre = mostrarSub ? `${s.spe_name} — ${s.sub_spe_name}` : s.spe_name;

      const precioBaseNum = Number(s?.precio_base_especialidad ?? NaN);
      const precioDoctorNum = Number(s?.precio_especialidad_doctor ?? NaN);

      return {
        id: s.sub_spe_id ?? s.spe_id,
        spe_id: s.spe_id,
        sub_spe_id: s.sub_spe_id,
        nombre,
        // opcionales, por si quieres mostrar después
        precioGeneral: isNaN(precioBaseNum) ? null : precioBaseNum,
        precioDoctor: isNaN(precioDoctorNum) ? null : precioDoctorNum,
        raw: s // aquí van id_precio_base_especialidad, id_precio_especialidad_doctor, etc.
      };
    });

    if (this.areasMedicas.length === 1) {
      this.patientForm.get('area_medica')?.setValue(this.areasMedicas[0]);
    }
  }

  getAcompanantesPaciente(useId: any): void {
    this.acompanantesPaciente = [];
    this.patientForm.patchValue({ acompaniante: null }, { emitEvent: false });

    if (!useId) return;

    this.loadingAcompanantes = true;

    this.usuarioService.getBenefiariosPaciente(useId)
      .then((resp: any) => {
        this.acompanantesPaciente = Array.isArray(resp)
          ? resp
          : Array.isArray(resp?.data)
            ? resp.data
            : [];
      })
      .catch((err) => {
        console.error('Error al obtener acompañantes:', err);
        this.acompanantesPaciente = [];
      })
      .finally(() => {
        this.loadingAcompanantes = false;
      });
  }

  private buildTarifasFromArea(area: any): any[] {
    if (!area) return [];

    const raw = area.raw || {};
    const tarifas: any[] = [];

    // 1) Tarifa base de la especialidad
    const precioBaseNum = Number(raw.precio_base_especialidad ?? NaN);
    if (!isNaN(precioBaseNum)) {
      tarifas.push({
        id: 'base',
        tipo: 'base',
        label: 'Tarifa base especialidad',
        monto: precioBaseNum,
        spe_id: area.spe_id,
        sub_spe_id: area.sub_spe_id ?? null,
        id_precio_base_especialidad: raw.id_precio_base_especialidad ?? null,
        id_precio_especialidad_doctor: null
      });
    }

    // 2) Tarifa asignada por el doctor
    const precioDoctorNum = Number(raw.precio_especialidad_doctor ?? NaN);
    if (!isNaN(precioDoctorNum)) {
      const doctorName = `${this.infoDoctor?.use_name || ''} ${this.infoDoctor?.use_paternal_surname || ''}`.trim();
      tarifas.push({
        id: 'doctor',
        tipo: 'doctor',
        label: doctorName ? `Tarifa ${doctorName}` : 'Tarifa doctor',
        monto: precioDoctorNum,
        spe_id: area.spe_id,
        sub_spe_id: area.sub_spe_id ?? null,
        id_precio_base_especialidad: null,
        id_precio_especialidad_doctor: raw.id_precio_especialidad_doctor ?? null
      });
    }

    // ✅ 3) Costo 0
    tarifas.push({
      id: 'costo0',
      tipo: 'costo0',
      label: 'Costo 0',
      monto: 0,
      spe_id: area.spe_id,
      sub_spe_id: area.sub_spe_id ?? null,
      id_precio_base_especialidad: null,
      id_precio_especialidad_doctor: null
    });

    // ✅ 4) Personalizada
    tarifas.push({
      id: 'custom',
      tipo: 'custom',
      label: 'Personalizada',
      monto: null, // se toma desde el input tarifa_custom_monto
      spe_id: area.spe_id,
      sub_spe_id: area.sub_spe_id ?? null,
      id_precio_base_especialidad: null,
      id_precio_especialidad_doctor: null
    });

    return tarifas;
  }



  // =========================================================
  // BÚSQUEDA / AUTOCOMPLETE (Step 1)
  // =========================================================
  private initAutocomplete(): void {
    this.searchCtrl.valueChanges.pipe(
      debounceTime(250),

      tap((v: any) => {
        const term = (v || '').toString().trim();

        if (!term) {
          this.clearPatientForm();
          this.showDropdown = false;
          this.tooShort = false;
          this.searchRutInvalid = false;
          this.resultados = [];
          this.infoRut = {};
          this.isNewUser = false;
          this.lockStep1Form();
          return;
        }

        this.showDropdown = true;
        this.tooShort = term.length < this.minSearchLen;
        this.loading = !this.tooShort;
      }),

      switchMap((v: string) => {
        const term = (v || '').toString().trim();

        if (!term || term.length < this.minSearchLen) {
          this.loading = false;
          return of([]);
        }

        return from(
          this.reservaDoctor.getVerificarPacienteAgenda({ rut: term })
        ).pipe(
          catchError(() => of([]))
        );
      })
    ).subscribe((resp: any) => {
      this.loading = false;

      this.resultados = Array.isArray(resp)
        ? resp
        : (resp ? [resp] : []);

      if (this.resultados.length > 0 && !this.isNewUser) {
        this.lockStep1Form();
      }
    });
  }


  onRutSearchInput(raw: string): void {
    const clean = this.normalizeRutDigits(raw);
    const formatted = this.formatRutWithDash(clean);

    this.searchCtrl.setValue(formatted, { emitEvent: true });

    // No se valida mientras escribe, para permitir coincidencias parciales.
    // La validación real se hace al presionar "Agregar usuario".
    this.searchRutInvalid = false;
  }


  onEnterSearch(): void {
    let rut = (this.searchCtrl.value || '').toString().trim();

    rut = this.formatRutWithDash(rut);
    this.searchCtrl.setValue(rut, { emitEvent: false });

    if (!rut || rut.length < this.minSearchLen) {
      this.showDropdown = !!rut;
      this.tooShort = !!rut;
      this.resultados = [];
      return;
    }

    this.loading = true;
    this.showDropdown = true;
    this.searchRutInvalid = false;

    this.reservaDoctor.getVerificarPacienteAgenda({ rut })
      .then((resp: any) => {
        this.resultados = Array.isArray(resp)
          ? resp
          : (resp ? [resp] : []);
      })
      .catch(() => {
        this.resultados = [];
      })
      .finally(() => {
        this.loading = false;
      });
  }

  normalizarRutComparacion(value: any): string {
    return (value || '')
      .toString()
      .replace(/\./g, '')
      .replace(/-/g, '')
      .toUpperCase()
      .replace(/[^0-9K]/g, '');
  }

  rutYaExisteExacto(): boolean {
    const rutBuscado = this.normalizarRutComparacion(this.searchCtrl.value);

    if (!rutBuscado) {
      return false;
    }

    return this.resultados.some((p: any) => {
      const rutPaciente = this.normalizarRutComparacion(p?.use_rut || p?.use_username);
      return rutPaciente === rutBuscado;
    });
  }

  puedeMostrarAgregarRutBuscado(): boolean {
    const typed = (this.searchCtrl.value || '').toString().trim();

    if (!typed || typed.length < this.minSearchLen) {
      return false;
    }

    return !this.rutYaExisteExacto();
  }


  seleccionarUsuario(item: any): void {
    const areaCode = this.areaCodes.find(
      (a: any) => String(a.cou_phone_code) === String(item?.use_cod_area)
    );

    if (areaCode) {
      this.selectedAreaCode = areaCode;
      this.setTelefonoValidators(areaCode);
    }

    this.isNewUser = false;
    this.searchRutInvalid = false;

    const raw = this.formatRutWithDash(item?.use_rut || item?.use_username || '');

    this.searchCtrl.setValue(raw, { emitEvent: false });
    this.infoRut = item;
    this.showDropdown = false;

    this.patientForm.patchValue({
      use_id: item?.use_id || '',
      use_rut: raw,
      use_name: item?.use_name || '',
      paterno: item?.use_paternal_surname || '',
      materno: item?.use_maternal_surname || '',
      nacimiento: this.toDateInput(item?.use_birthdate),
      email: item?.use_email || '',
      telefono: item?.use_movil_phone || item?.use_phone || '',
      telefonoCodigo: this.normArea(item?.use_cod_area) || '56'
    }, { emitEvent: false });

    this.unlockStep1Form();
    this.patientForm.updateValueAndValidity();

    this.getAcompanantesPaciente(item?.use_id);
  }

  prepararNuevoUsuario(): void {
    const typed = (this.searchCtrl.value || '').toString().trim();

    if (!typed) {
      this.lockStep1Form();
      return;
    }

    if (this.rutYaExisteExacto()) {
      this.searchRutInvalid = false;
      this.lockStep1Form();
      return;
    }

    const rutValido = this.generalService.validaRutChileno(typed);

    if (!rutValido) {
      this.searchRutInvalid = true;
      this.showDropdown = true;
      this.isNewUser = false;
      this.clearPatientForm();
      this.lockStep1Form();
      return;
    }

    this.searchRutInvalid = false;
    this.isNewUser = true;
    this.showDropdown = false;
    this.resultados = [];

    this.patientForm.reset({
      use_id: '',
      use_rut: typed,
      use_name: '',
      paterno: '',
      materno: '',
      nacimiento: '',
      telefonoCodigo: this.selectedAreaCode?.cou_phone_code || '56',
      telefono: '',
      email: '',

      sch_date: '',
      sch_hou_id: null,
      sch_from: '',
      sch_to: '',

      area_medica: null,
      acompaniante: null,
      motivo: '',
      tarifa: null,
      tarifa_custom_monto: null
    });

    this.unlockStep1Form();
    this.patientForm.updateValueAndValidity();
  }

  // =========================================================
  // VALIDACIONES / INPUTS
  // =========================================================
  allowOnlyLetters(event: KeyboardEvent) {
    const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ'´\s]*$/;
    if (!regex.test(event.key)) event.preventDefault();
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    if (!/[0-9]/.test(event.key)) event.preventDefault();
  }

  allowOnlyRutChars(event: KeyboardEvent): void {
    const key = event.key;

    if (
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'Tab' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'Home' ||
      key === 'End'
    ) {
      return;
    }

    if (!/^[0-9kK]$/.test(key)) {
      event.preventDefault();
    }
  }

  onRutPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text') || '';

    if (!/^[0-9kK.-]+$/.test(pastedText)) {
      event.preventDefault();
    }
  }

  normalizeRutDigits(v: string): string {
    return (v || '')
      .toString()
      .replace(/\./g, '')
      .replace(/-/g, '')
      .toUpperCase()
      .replace(/[^0-9K]/g, '');
  }

  formatRutWithDash(v: string): string {
    const clean = this.normalizeRutDigits(v);

    if (clean.length <= 1) {
      return clean;
    }

    return clean.slice(0, -1) + '-' + clean.slice(-1);
  }

  onRutBlur(): void {
    const ctrl = this.patientForm.get('use_rut');
    const val = (ctrl?.value || '').toString();
    ctrl?.setValue(this.formatRutWithDash(val), { emitEvent: false });
  }

  onAreaCodeChange(event: any): void {
    const selectedCode = event?.target?.value;

    const match = this.areaCodes.find(
      (a: any) => String(a.cou_phone_code) === String(selectedCode)
    );

    if (!match) {
      return;
    }

    this.selectedAreaCode = match;
    this.setTelefonoValidators(match);
  }

  private setTelefonoValidators(areaCode: any): void {
    const telCtrl = this.patientForm.get('telefono');

    if (!telCtrl) {
      return;
    }

    const min = Number(areaCode?.cou_min_phone || 0);
    const max = Number(areaCode?.cou_max_phone || 15);

    telCtrl.setValidators([
      Validators.required,
      Validators.pattern(/^[0-9]*$/),
      Validators.minLength(min),
      Validators.maxLength(max)
    ]);

    telCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private step1Controls = ['use_rut', 'use_name', 'paterno', 'nacimiento', 'telefonoCodigo', 'telefono', 'email'];
  private markStep1Touched(): void { this.step1Controls.forEach(c => this.patientForm.get(c)?.markAsTouched()); }

  isStep1Valid(): boolean {
    return this.patientForm.valid && this.step1Unlocked && !this.searchRutInvalid;
  }


  // =========================================================
  // NAVEGACIÓN (sin insertar/actualizar)
  // =========================================================
  goToStep2() { this.steps = { searchPatient: false, selectDate: true, payment: false, showSummary: false }; }
  goToStep3() { this.steps = { searchPatient: false, selectDate: false, payment: true, showSummary: false }; }
  goToStep4() { this.steps = { searchPatient: false, selectDate: false, payment: false, showSummary: true }; }
  volverStep1() { this.steps = { searchPatient: true, selectDate: false, payment: false, showSummary: false }; }
  volverStep2() { this.steps = { searchPatient: false, selectDate: true, payment: false, showSummary: false }; }
  volverStep3() { this.steps = { searchPatient: false, selectDate: false, payment: true, showSummary: false }; }

  // Botón “Siguiente” del Paso 1 → Paso 2
  nextFromStep1() {
    console.log(this.patientForm.value);
    this.markStep1Touched();
    if (!this.isStep1Valid()) return;
    this.goToStep2();
  }

  // Paso 2 → Paso 3
  canProceedFromStep2(): boolean {
    const v = this.patientForm.value as any;
    return !!v.sch_date && (!!v.sch_hou_id || (!!v.sch_from && !!v.sch_to));
  }

  nextFromStep2() {
    console.log(this.patientForm.value);
    if (!this.canProceedFromStep2()) return;
    this.goToStep3();
  }

  // Paso 3 → Paso 4 (resumen)
  canProceedFromStep3(): boolean {
    const v = this.patientForm.value as any;

    if (!v.area_medica || !v.tarifa) return false;

    if (v.tarifa?.tipo === 'custom') {
      const m = Number((v.tarifa_custom_monto || '').toString().replace(/\D/g, ''));
      return !isNaN(m) && m >= 0 && m <= 1000000;
    }

    return true;
  }


  nextFromStep3() {
    console.log(this.patientForm.value);

    if (!this.canProceedFromStep3()) return;
    this.goToStep4();
  }

  // =========================================================
  // ARCHIVOS (STEP 3) – solo manejo local, sin envío aún
  // =========================================================
  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input?.files?.length) return;
    const MAX_MB = 10;
    const nextFiles: File[] = [];
    Array.from(input.files).forEach(f => {
      const sizeMb = f.size / (1024 * 1024);
      if (sizeMb <= MAX_MB) nextFiles.push(f);
    });
    this.selectedFiles = [...this.selectedFiles, ...nextFiles];
    input.value = '';
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
    this.selectedFiles = [...this.selectedFiles];
    this.documentoNombreResumen = null;
  }

  // =========================================================
  // PAYLOAD FINAL Y ACCIÓN (insert / update / '')
  // =========================================================

  // Devuelve '', 'insert' o 'update' según la lógica de cambios en Step 1
  private getAccionPaciente(): '' | 'insert' | 'update' {
    const v = this.patientForm.value as any;

    // Caso 1: no tiene use_id → usuario nuevo
    if (!v.use_id) {
      return 'insert';
    }

    // Caso 2: tenemos use_id pero no infoRut (por seguridad lo tratamos como insert)
    const u = this.infoRut || {};
    if (!u || !u.use_id) {
      return 'insert';
    }

    // Helpers de normalización
    const s = (x: any) => (x ?? '').toString().trim();
    const lower = (x: any) => s(x).toLowerCase();
    const onlyDigits = (x: any) => (x ?? '').toString().replace(/[^\d]/g, '');

    // RUT (por si acaso)
    const sameRut =
      this.normRutForCompare(v.use_rut) ===
      this.normRutForCompare(u.use_rut || u.use_username);

    const sameName = s(v.use_name) === s(u.use_name);
    const samePaterno = s(v.paterno) === s(u.use_paternal_surname);
    const sameMaterno = s(v.materno) === s(u.use_maternal_surname || '');

    // Fecha de nacimiento: en el form es 'YYYY-MM-DD'
    const originalBirth = this.toDateInput(u.use_birthdate);
    const sameBirth = s(v.nacimiento) === originalBirth;

    // Teléfono y código de área
    const sameCodArea =
      onlyDigits(v.telefonoCodigo) === onlyDigits(u.use_cod_area);

    const originalPhone = u.use_phone || u.use_movil_phone;
    const samePhone =
      onlyDigits(v.telefono) === onlyDigits(originalPhone);

    // Email
    const sameEmail = lower(v.email) === lower(u.use_email);

    const allSame =
      sameRut &&
      sameName &&
      samePaterno &&
      sameMaterno &&
      sameBirth &&
      sameCodArea &&
      samePhone &&
      sameEmail;

    // Usuario existente sin cambios → ''
    if (allSame) return '';

    // Usuario existente con cambios → 'update'
    return 'update';
  }

  // Construye el payload final { datosPaciente, datosReserva }
  buildReservaPayload() {

    const v = this.patientForm.value as any;
    const doc = this.infoDoctor || {};
    const area = v.area_medica || null;
    const tarifa = v.tarifa || null;
    const rawArea = area?.raw || {};

    const accion = this.getAccionPaciente();

    // Definimos qué ID va en base a la tarifa seleccionada
    let idBase: number | null = null;
    let idDoc: number | null = null;

    if (tarifa?.tipo === 'base') {
      idBase = rawArea.id_precio_base_especialidad ?? null;
      idDoc = null;
    } else if (tarifa?.tipo === 'doctor') {
      idBase = null;
      idDoc = rawArea.id_precio_especialidad_doctor ?? null;
    } else {
      // costo0 o custom
      idBase = null;
      idDoc = null;
    }

    // ✅ Monto final a enviar
    const montoFinal =
      tarifa?.tipo === 'custom'
        ? Number((v.tarifa_custom_monto || '').toString().replace(/\D/g, ''))
        : Number(tarifa?.monto ?? 0);

    return {
      datosPaciente: {
        use_rut: v.use_rut,
        use_name: v.use_name,
        use_paternal_surname: v.paterno,
        use_maternal_surname: v.materno || '',
        use_birthdate: v.nacimiento,
        use_cod_area: v.telefonoCodigo,
        use_movil_phone: v.telefono,
        use_email: v.email,
        accion: accion,                            // '', 'insert' o 'update'
        use_id: v.use_id ? String(v.use_id) : null,
        doc_id: doc.doc_id ?? null
      },
      datosReserva: {
        doc_id: doc.doc_id ?? null,                            // id doctor
        doc_name: doc.use_name || null,                        // nombre doctor
        doc_last_name: doc.use_paternal_surname || null,       // apellido doctor
        fecha_reserva: v.sch_date,                             // fecha reserva (YYYY-MM-DD)
        hora_reserva: v.sch_from,                              // hora reserva
        sch_hou_id: v.sch_hou_id,                              // id de la hora
        tel_doc_app_boo_spe_id: area?.sub_spe_id ?? area?.spe_id ?? null,
        spe_name: rawArea.spe_name || area?.nombre || null,    // nombre especialidad
        tel_doc_reason: v.motivo || '',
        monto_pagar: montoFinal,                               // ✅ aquí va el final
        id_precio_base_especialidad: idBase,
        id_precio_especialidad_doctor: idDoc,
        tel_doc_sta_id: null,
        doc_email: doc.use_email || null,
        acompaniante: v.acompaniante || null,
        acompaniante_use_id: v.acompaniante?.use_id || null,
      }
    };
  }



  loadingSubmit = false;
  submitError: string | null = null;
  maskedEmail: string = '';


  // Si quieres usarlo desde el Step 4:
  async onConfirmAppointment() {
    // Evita doble click
    if (this.loadingSubmit) return;

    const payloadBase = this.buildReservaPayload();
    console.log('===== PAYLOAD BASE (SIN ADJUNTOS) =====');
    console.log(JSON.stringify(payloadBase, null, 2));

    this.loadingSubmit = true;
    this.submitError = null;

    // ✅ Abrir modal de cargando ANTES de los await pesados

    Swal.fire({
      allowOutsideClick: false,
      html: `<div style="margin: 40px 0;">
                          <i class="material-icons" style="color:#2BABB7; font-size: 58px; margin-bottom:25px">schedule</i>
                          <h1 style="font-family: Mulish; font-style: normal; font-weight: 700; font-size: 24px; line-height: 32px; text-align: center; color: #171994;">
                              Generando reserva ...
                          </h1>
                       </div>`,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading(); // Muestra el spinner de carga
      }
    });

    // ✅ Deja respirar al DOM para que el Swal se pinte antes de continuar
    await new Promise(resolve => setTimeout(resolve, 0));

    try {
      const documentosAdjuntos = await this.buildDocumentosAdjuntos();

      const payload = {
        ...payloadBase,
        documentosAdjuntos
      };

      console.log('===== PAYLOAD FINAL A ENVIAR (CON ADJUNTOS) =====');
      console.log(JSON.stringify(payload, null, 2));

      const resp = await this.reservaDoctor.postFinalizarAgendamientoAgendaDoctor(payload);
      console.log('Respuesta backend:', resp);

      // Email enmascarado para el mensaje
      const email = (this.patientForm.value as any).email || '';
      this.maskedEmail = this.maskEmail(email);

      // Mostrar card de éxito
      this.showSuccessMessage = true;

      // Opcional: limpiar steps para que no interfieran
      this.steps = { searchPatient: false, selectDate: false, payment: false, showSummary: false };

      // ✅ Cerrar loading
      Swal.close();

      Swal.fire({
        allowOutsideClick: false,
        timer: 3000,
        timerProgressBar: false,
        showConfirmButton: false,
        html: `<div style="margin: 40px 0;">
            <i class="material-icons" style="color:#2BABB7; font-size: 58px; margin-bottom:25px">mood</i>
            <h1 style="font-family: Mulish; font-style: normal; font-weight: 700; font-size: 24px; line-height: 32px; text-align: center; color: #171994;">
                Reserva realizada
            </h1>
         </div>`
      });


    } catch (err) {
      console.error(err);
      this.submitError = 'Ocurrió un error al finalizar el agendamiento. Intenta nuevamente.';

      // ✅ Cerrar loading
      Swal.close();

      // ✅ Mostrar error
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: this.submitError,
        confirmButtonText: 'Aceptar'
      });

    } finally {
      this.loadingSubmit = false;
    }
  }

  private maskEmail(email: string): string {
    if (!email) { return ''; }

    const [localPart, domainPart] = email.split('@');
    if (!domainPart) { return email; }

    // local: primeras 3 letras + *****
    const visibleLocal = localPart.slice(0, 3);
    const maskedLocal = `${visibleLocal}*****`;

    // dominio: primeras 4 letras + ** (antes del .)
    const [domainName, tld] = domainPart.split('.');
    const visibleDomain = domainName.slice(0, 4);
    const maskedDomain = `${visibleDomain}**`;

    return `${maskedLocal}@${maskedDomain}${tld ? '.' + tld : ''}`;
  }

  // Convierte un File a base64 (sin el prefijo data:...)
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // result suele ser "data:application/pdf;base64,AAAA..."
        const base64 = result.split(',')[1] || result;
        resolve(base64);
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }

  // Construye el arreglo de documentos adjuntos para el payload
  private async buildDocumentosAdjuntos() {
    const documentos: any[] = [];

    for (const item of this.selectedFiles) {
      const file = item.file || item; // por si en algún momento queda un File "puro"
      const base64 = await this.fileToBase64(file);

      documentos.push({
        file_name: item.finalName || file.name, // 👈 usa el nombre custom
        mime_type: file.type,
        size: file.size,
        content_base64: base64
      });
    }

    return documentos;
  }


  // =========================================================
  // HELPERS
  // =========================================================
  private normArea(v: any): string { return (v ?? '').toString().replace(/[^\d]/g, ''); }



  private toDateInput(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private clearPatientForm() {
    this.patientForm.reset({
      use_id: '',
      use_rut: '',
      use_name: '',
      paterno: '',
      materno: '',
      nacimiento: '',
      telefonoCodigo: '56',
      telefono: '',
      email: '',
      sch_date: '',
      sch_hou_id: null,
      sch_from: '',
      sch_to: '',
      area_medica: null,
      acompaniante: null,
      motivo: '',
      tarifa: null
    });

    this.infoRut = {};
    this.isNewUser = false;

    const telCodCtrl = this.patientForm.get('telefonoCodigo');
    telCodCtrl?.clearValidators();
    telCodCtrl?.updateValueAndValidity();

    this.patientForm.markAsPristine();
    this.patientForm.markAsUntouched();

    this.lockStep1Form();
  }

  private lockRut() { this.isRutLocked = true; }
  private unlockRut() { this.isRutLocked = false; }

  // Eventos que el calendario hijo puede emitir
  onDateSelected(dateStr: string) {
    this.patientForm.patchValue({ sch_date: dateStr, sch_hou_id: null, sch_from: '', sch_to: '' });
  }

  onSlotSelected(slot: { id: number | string; from: string; to: string }) {
    this.patientForm.patchValue({ sch_hou_id: slot.id, sch_from: slot.from, sch_to: slot.to });
  }

  // Cerrar dropdown si se hace click fuera
  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent) {
    const el = ev.target as HTMLElement;
    if (!el.closest('.autocomplete-container')) this.showDropdown = false;
  }

  // (Utilidades de comparación si más adelante te sirven)
  private s(v: any): string { return (v ?? '').toString().trim(); }

  private toYMD(x: any): string {
    if (!x) return '';
    const d = new Date(x); if (isNaN(d.getTime())) return '';
    const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }

  private normRutForCompare(rut: any): string {
    const clean = (rut || '').toString().replace(/[^0-9kK]/g, '').toUpperCase();
    if (!clean) return '';
    if (clean.length === 1) return clean;
    const cuerpo = clean.slice(0, -1);
    const dv = clean.slice(-1);
    return `${cuerpo}-${dv}`;
  }

  onNewAppointment() {
    this.clearPatientForm();
    this.selectedFiles = [];
    this.documentoNombreResumen = null;   // 👈 limpiar
    this.resultados = [];
    this.searchCtrl.setValue('', { emitEvent: false });
    this.showDropdown = false;
    this.showSuccessMessage = false;

    this.steps = { searchPatient: true, selectDate: false, payment: false, showSummary: false };
  }


  goToDashboard() {
    this.router.navigate(['/dashboard']); // cambia '/dashboard' si tu ruta es distinta
  }

  flagDropdownOpen = false;

  toggleFlagDropdown() {
    this.flagDropdownOpen = !this.flagDropdownOpen;
  }

  selectAreaCode(areaCode: any) {
    this.selectedAreaCode = areaCode;
    this.patientForm.patchValue({ telefonoCodigo: areaCode.cou_phone_code });
    this.flagDropdownOpen = false;

    // Volver a aplicar validación de longitud del teléfono
    this.onAreaCodeChange({ target: { value: areaCode.cou_phone_code } });
  }


  // Abrir modal
  openDocumentoModal() {
    this.modalDocForm.reset();
    this.modalFileNamePreview = '';
    this.showDocumentoModal = true;
  }

  // Cerrar modal
  closeDocumentoModal() {
    this.showDocumentoModal = false;
  }

  // Cuando se selecciona archivo dentro del modal
  onModalFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      this.modalDocForm.patchValue({ file: null });
      this.modalFileNamePreview = '';
      return;
    }

    const file = input.files[0];
    this.modalDocForm.patchValue({ file });
    this.modalFileNamePreview = file.name;
  }

  // Confirmar (guardar doc con nombre custom)
  confirmarDocumento() {
    if (this.modalDocForm.invalid) {
      this.modalDocForm.markAllAsTouched();
      return;
    }

    const { displayName, file } = this.modalDocForm.value as any;

    const baseName = (displayName || '').toString().trim();
    const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
    const finalName = ext ? `${baseName}.${ext}` : baseName;

    // 👉 Guardamos UN solo documento con nombre custom
    this.selectedFiles = [{
      file,
      finalName
    }];

    // 👉 Nombre que se usará en el resumen (Step 4)
    this.documentoNombreResumen = finalName;

    this.showDocumentoModal = false;
  }

  private lockStep1Form(): void {
    this.step1Unlocked = false;

    this.step1EditableControls.forEach(ctrlName => {
      this.patientForm.get(ctrlName)?.disable({ emitEvent: false });
    });

    this.patientForm.get('use_rut')?.enable({ emitEvent: false });
    this.isRutLocked = false;
  }

  private unlockStep1Form(lockRut: boolean = true): void {
    this.step1Unlocked = true;

    this.step1EditableControls.forEach(ctrlName => {
      this.patientForm.get(ctrlName)?.enable({ emitEvent: false });
    });

    this.patientForm.get('use_rut')?.enable({ emitEvent: false });
    this.isRutLocked = lockRut;
  }

  onTarifaCustomInput(event: any): void {
    let value = event.target.value || '';

    // dejar solo números
    value = value.toString().replace(/\D/g, '');

    // máximo 1.000.000
    let numericValue = Number(value || 0);
    if (numericValue > 1000000) {
      numericValue = 1000000;
    }

    // si está vacío
    if (!value) {
      this.patientForm.get('tarifa_custom_monto')?.setValue('', { emitEvent: false });
      return;
    }

    // formatear con puntos de miles
    const formatted = '$' + numericValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    this.patientForm.get('tarifa_custom_monto')?.setValue(formatted, { emitEvent: false });
  }

  maxMontoValidator(max: number) {
    return (control: any) => {
      const rawValue = control.value || '';
      const numericValue = Number(rawValue.toString().replace(/\D/g, ''));

      if (isNaN(numericValue)) {
        return { invalidMonto: true };
      }

      if (numericValue > max) {
        return { maxMonto: true };
      }

      return null;
    };
  }
}
